import { addDays, isForgotten, localClockOf, localDateOf, startOfLocalDate } from './shifts'
import type { LocalDate } from './shifts'
import type { Instant, UserId } from './types'

/**
 * Availability and requests to come in: **when a technician can work, and an
 * engineer asking them to.**
 *
 * The whole point is fewer phone calls. A technician says which of the next
 * two weeks they can do; an engineer asks for a day; the answer is a tap. So
 * nothing here is compulsory — a day nobody has said anything about is
 * *unknown*, not a problem, and asking somebody in on a day they marked off is
 * allowed (with a warning) because people's plans change and the tool should
 * not be what stops a phone call that would have worked.
 *
 * Two different kinds of time, and they are stored differently on purpose:
 *
 *  - **Availability is wall-clock** — "Tuesday, 9 to 5" — a local date and two
 *    `HH:MM` strings. It is a statement about a day, and it means the same
 *    thing whatever the zone's offset that day.
 *  - **A request is two instants**, computed once from a local date and times
 *    in the configured zone (`localInstant`). It is an appointment: it is in
 *    the shift report's timeline, and it must sort and compare against shifts.
 *
 * Every rule takes `now`, `today` and the zone as parameters, like the rest of
 * this package.
 */

const MINUTE = 60_000

// --- Wall-clock times -------------------------------------------------------

/** `09:00`: a time on a local date, 24-hour, minute precision. */
export type ClockTime = string & { readonly __brand: 'ClockTime' }

/**
 * `9:00`, `09:00` and Postgres's `09:00:00` all read as `09:00`. Anything that
 * is not a real time of day is null — `24:00` included, because a window that
 * ends at midnight is a window that ends on another date.
 */
export function parseClock(value: unknown): ClockTime | null {
  if (typeof value !== 'string') return null
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim())
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}` as ClockTime
}

export function clockMinutes(t: ClockTime): number {
  const [h, m] = t.split(':').map(Number)
  return (h ?? 0) * 60 + (m ?? 0)
}

export interface TimeWindow {
  readonly from: ClockTime
  readonly to: ClockTime
}

/** What a window is shown as everywhere: `09:00–17:00`. */
export function windowLabel(w: TimeWindow): string {
  return `${w.from}–${w.to}`
}

/** The window a technician gets before they have ever set one. */
export const DEFAULT_WINDOW: TimeWindow = { from: '09:00' as ClockTime, to: '17:00' as ClockTime }

/** How far ahead the availability list runs on the phone. */
export const AVAILABILITY_DAYS = 14
/** How far ahead anything may be set or requested. Past this is a typo in the date. */
export const SCHEDULE_HORIZON_DAYS = 56
/** A note is a word or two — "dentist pm", "Assemble arms". Longer is clamped, not refused. */
export const NOTE_MAX = 140

/** Trimmed, clamped, and null when empty. */
export function cleanNote(note: unknown): string | null {
  if (typeof note !== 'string') return null
  const t = note.trim().replace(/\s+/g, ' ')
  return t === '' ? null : t.slice(0, NOTE_MAX)
}

// --- Local dates ------------------------------------------------------------

function utcDay(date: LocalDate): number {
  const [y, m, d] = date.split('-').map(Number)
  return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1) / 86_400_000
}

/** Whole days from `a` to `b`: positive when `b` is later. */
export function daysBetween(a: LocalDate, b: LocalDate): number {
  return Math.round(utcDay(b) - utcDay(a))
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayOf(date: LocalDate): number {
  return (((Math.round(utcDay(date)) + 4) % 7) + 7) % 7
}

/** `count` consecutive dates starting at `from`. */
export function datesFrom(from: LocalDate, count: number): readonly LocalDate[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => addDays(from, i))
}

/** Monday to Sunday of the calendar week after the one `today` is in. */
export function nextWeekOf(today: LocalDate): { readonly from: LocalDate; readonly to: LocalDate } {
  const toMonday = ((8 - weekdayOf(today)) % 7) || 7
  const from = addDays(today, toMonday)
  return { from, to: addDays(from, 6) }
}

/**
 * The instant a wall-clock time happens on a local date, in a zone.
 *
 * Built from the shift helpers: start from local midnight plus the minutes,
 * then read the clock back and correct by the difference. Adding minutes to
 * midnight alone is an hour out on the two days a year the offset changes —
 * 09:00 on the morning the clocks go back is 17:00Z, not 16:00Z.
 */
export function localInstant(date: LocalDate, clock: ClockTime, timeZone: string): Instant {
  const wanted = clockMinutes(clock)
  let t = Date.parse(startOfLocalDate(date, timeZone)) + wanted * MINUTE
  for (let i = 0; i < 2; i++) {
    const iso = new Date(t).toISOString() as Instant
    const read = daysBetween(date, localDateOf(iso, timeZone)) * 1440 + clockMinutes(localClockOf(iso, timeZone) as ClockTime)
    if (read === wanted) break
    t += (wanted - read) * MINUTE
  }
  return new Date(t).toISOString() as Instant
}

// --- Availability -----------------------------------------------------------

export type AvailabilityStatus = 'available' | 'unavailable'

/** One person, one day. No row at all means "not said" — unknown, not off. */
export interface DayAvailability {
  readonly userId: UserId
  readonly date: LocalDate
  readonly status: AvailabilityStatus
  /** The hours they can do. Null when unavailable. */
  readonly window: TimeWindow | null
  readonly note: string | null
  readonly updatedAt: Instant
}

/** A change to one day. `status: null` clears it back to unknown. */
export interface AvailabilityEdit {
  readonly date: LocalDate
  readonly status: AvailabilityStatus | null
  readonly window?: { readonly from: string; readonly to: string } | null
  readonly note?: string | null
}

export type ScheduleRefusal =
  | 'bad_date'
  | 'bad_time'
  | 'ends_before_it_starts'
  | 'in_the_past'
  | 'too_far_ahead'
  /** Answering a request that has been cancelled, or cancelling a declined one. */
  | 'cancelled'
  | 'already_answered'
  | 'declined'
  | 'already_over'
  /** Backing out of an accepted request once it has begun. */
  | 'already_started'
  | 'overlaps'

export const SCHEDULE_REFUSAL_MESSAGE: Readonly<Record<ScheduleRefusal, string>> = {
  bad_date: 'That is not a date.',
  bad_time: 'Times are written like 09:00.',
  ends_before_it_starts: 'The end time has to be after the start time.',
  in_the_past: 'That day has already gone.',
  too_far_ahead: `Only the next ${SCHEDULE_HORIZON_DAYS / 7} weeks can be planned.`,
  cancelled: 'That request was cancelled.',
  already_answered: 'That request has already been answered.',
  declined: 'That request was declined, so there is nothing to cancel.',
  already_over: 'That time has already passed.',
  already_started: 'That shift has already started — call to say you can’t make it.',
  overlaps: 'They already have a request at that time. Cancel it first.',
}

const isLocalDate = (v: unknown): v is LocalDate =>
  typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) && addDays(v as LocalDate, 0) === v

type Settled<T> = ({ readonly ok: true } & T) | { readonly ok: false; readonly reason: ScheduleRefusal }

function settleWindow(raw: { readonly from: unknown; readonly to: unknown } | null | undefined): Settled<{ window: TimeWindow }> {
  const from = parseClock(raw?.from)
  const to = parseClock(raw?.to)
  if (!from || !to) return { ok: false, reason: 'bad_time' }
  if (clockMinutes(to) <= clockMinutes(from)) return { ok: false, reason: 'ends_before_it_starts' }
  return { ok: true, window: { from, to } }
}

function settleDate(date: unknown, today: LocalDate): Settled<{ date: LocalDate }> {
  if (!isLocalDate(date)) return { ok: false, reason: 'bad_date' }
  if (date < today) return { ok: false, reason: 'in_the_past' }
  if (daysBetween(today, date) > SCHEDULE_HORIZON_DAYS) return { ok: false, reason: 'too_far_ahead' }
  return { ok: true, date }
}

export interface SettledAvailability {
  readonly date: LocalDate
  /** Null: delete the row. */
  readonly status: AvailabilityStatus | null
  readonly window: TimeWindow | null
  readonly note: string | null
}

/**
 * One day's edit, checked. Past days are refused (yesterday's availability is
 * history, and nothing reads it but "repeat last week"); an unavailable day
 * drops whatever window was sent, rather than refusing a harmless leftover.
 */
export function settleAvailability(edit: AvailabilityEdit, today: LocalDate): Settled<{ day: SettledAvailability }> {
  const date = settleDate(edit.date, today)
  if (!date.ok) return date
  if (edit.status === null) return { ok: true, day: { date: date.date, status: null, window: null, note: null } }
  if (edit.status === 'unavailable') {
    return { ok: true, day: { date: date.date, status: 'unavailable', window: null, note: cleanNote(edit.note) } }
  }
  if (edit.status !== 'available') return { ok: false, reason: 'bad_time' }
  const window = settleWindow(edit.window)
  if (!window.ok) return window
  return { ok: true, day: { date: date.date, status: 'available', window: window.window, note: cleanNote(edit.note) } }
}

/** One person's days from `today`, each with what they said or null. */
export function availabilityDays(
  today: LocalDate,
  count: number,
  mine: readonly DayAvailability[],
): readonly { readonly date: LocalDate; readonly availability: DayAvailability | null }[] {
  const byDate = new Map(mine.map((a) => [a.date, a]))
  return datesFrom(today, count).map((date) => ({ date, availability: byDate.get(date) ?? null }))
}

/**
 * The window to offer for a day they have not set: the one they last saved,
 * because people keep the same hours. 9–5 before they have saved any.
 */
export function lastWindow(mine: readonly DayAvailability[]): TimeWindow {
  let best: DayAvailability | null = null
  for (const a of mine) {
    if (a.window && (!best || a.updatedAt > best.updatedAt)) best = a
  }
  return best?.window ?? DEFAULT_WINDOW
}

/**
 * "Weekdays, these hours, for the next two weeks" — for the days not yet set.
 *
 * **It never overwrites.** A day already marked, off for the dentist or
 * available till noon, is the person's own decision; a quick action that
 * quietly undoes it is the kind of tool people stop trusting.
 */
export function fillWeekdays(
  today: LocalDate,
  count: number,
  window: TimeWindow,
  mine: readonly DayAvailability[],
): readonly AvailabilityEdit[] {
  const set = new Set(mine.map((a) => a.date))
  return datesFrom(today, count)
    .filter((d) => !set.has(d) && weekdayOf(d) >= 1 && weekdayOf(d) <= 5)
    .map((date) => ({ date, status: 'available', window, note: null }))
}

/**
 * "Same as last week": each unset day takes what the same weekday a week
 * earlier says — and that day may itself have just been filled, so one week
 * of answers repeats down the whole list. Notes are not copied: "dentist" was
 * about one Tuesday, not every Tuesday.
 */
export function repeatLastWeek(
  today: LocalDate,
  count: number,
  mine: readonly DayAvailability[],
): readonly AvailabilityEdit[] {
  const value = new Map<LocalDate, { status: AvailabilityStatus; window: TimeWindow | null }>()
  for (const a of mine) value.set(a.date, { status: a.status, window: a.window })
  const edits: AvailabilityEdit[] = []
  for (const date of datesFrom(today, count)) {
    if (value.has(date)) continue
    const source = value.get(addDays(date, -7))
    if (!source) continue
    value.set(date, source)
    edits.push({ date, status: source.status, window: source.window, note: null })
  }
  return edits
}

/** Today up to the horizon: the days availability can be set for. */
export function isSchedulable(date: LocalDate, today: LocalDate): boolean {
  return date >= today && daysBetween(today, date) <= SCHEDULE_HORIZON_DAYS
}

/**
 * One setting for several days at once, from the calendar. `note: undefined`
 * keeps each day's own note — "make these four available" should not wipe the
 * "dentist pm" on one of them.
 */
export interface AvailabilityChoice {
  readonly status: AvailabilityStatus | null
  readonly window?: { readonly from: string; readonly to: string } | null
  readonly note?: string | null
}

/**
 * The edits that apply one choice to a set of days, soonest first and once
 * each. Days that cannot be set — gone, or past the horizon — are **dropped,
 * not refused**: a fortnight dragged across a calendar that happens to start
 * on yesterday should set the thirteen days that can be, not none.
 */
export function applyAvailability(
  dates: readonly LocalDate[],
  choice: AvailabilityChoice,
  mine: readonly DayAvailability[],
  today: LocalDate,
): readonly AvailabilityEdit[] {
  const notes = new Map(mine.map((a) => [a.date, a.note]))
  return [...new Set(dates)]
    .filter((date) => isSchedulable(date, today))
    .sort()
    .map((date): AvailabilityEdit => {
      if (choice.status === null) return { date, status: null }
      const note = choice.note === undefined ? (notes.get(date) ?? null) : choice.note
      return choice.status === 'available'
        ? { date, status: 'available', window: choice.window ?? null, note }
        : { date, status: 'unavailable', window: null, note }
    })
}

/**
 * What a set of days has in common, to open the editor on: one status and one
 * window when they all agree, `'mixed'` when they do not. A single day is
 * simply its own setting.
 */
export function commonAvailability(
  dates: readonly LocalDate[],
  mine: readonly DayAvailability[],
): {
  readonly status: AvailabilityStatus | null | 'mixed'
  readonly window: TimeWindow | null | 'mixed'
  readonly note: string | null | 'mixed'
} {
  const byDate = new Map(mine.map((a) => [a.date, a]))
  const days = [...new Set(dates)].map((date) => byDate.get(date) ?? null)
  const same = <T>(values: readonly T[], key: (v: T) => string): T | 'mixed' | null => {
    if (values.length === 0) return null
    const first = values[0] as T
    return values.every((v) => key(v) === key(first)) ? first : 'mixed'
  }
  const windows = days.map((a) => a?.window ?? null)
  return {
    status: same(days.map((a) => a?.status ?? null), String),
    window: same(windows, (w) => (w ? windowLabel(w) : '')),
    note: same(days.map((a) => a?.note ?? null), String),
  }
}

// --- Requests to come in ----------------------------------------------------

export type RequestState = 'pending' | 'accepted' | 'declined' | 'cancelled'

/** An engineer asking one technician to come in, for one stretch of one day. */
export interface ComeInRequest {
  readonly id: string
  readonly technicianId: UserId
  readonly requestedBy: UserId
  readonly startsAt: Instant
  readonly endsAt: Instant
  /** What for — "Assemble arms". May be empty. */
  readonly note: string
  readonly state: RequestState
  /** The technician's answer, if they wrote one. */
  readonly reply: string | null
  readonly createdAt: Instant
  readonly answeredAt: Instant | null
  readonly cancelledAt: Instant | null
  /**
   * Set when an accepted request was declined afterwards — "can't make it".
   * The engineer was counting on this one, so it is shown to them until the
   * day has passed rather than simply turning grey on the grid.
   */
  readonly backedOutAt: Instant | null
}

type Timed = Pick<ComeInRequest, 'startsAt' | 'endsAt' | 'state'>

/** Accepted, then declined: the one declined request somebody needs to notice. */
export const backedOut = (r: Pick<ComeInRequest, 'state' | 'backedOutAt'>): boolean =>
  r.state === 'declined' && r.backedOutAt !== null

export interface RequestDraft {
  readonly date: unknown
  readonly from: unknown
  readonly to: unknown
  readonly note?: unknown
}

/**
 * A request's local date and times, turned into the two instants it is stored
 * as. Today is fine, as long as the stretch has not already ended — "can you
 * come in this afternoon?" is the most useful request there is.
 */
export function settleRequest(
  draft: RequestDraft,
  now: Instant,
  timeZone: string,
): Settled<{ date: LocalDate; startsAt: Instant; endsAt: Instant; note: string }> {
  const date = settleDate(draft.date, localDateOf(now, timeZone))
  if (!date.ok) return date
  const window = settleWindow({ from: draft.from, to: draft.to })
  if (!window.ok) return window
  const startsAt = localInstant(date.date, window.window.from, timeZone)
  const endsAt = localInstant(date.date, window.window.to, timeZone)
  if (Date.parse(endsAt) <= Date.parse(now)) return { ok: false, reason: 'already_over' }
  return { ok: true, date: date.date, startsAt, endsAt, note: cleanNote(draft.note) ?? '' }
}

/** The local date a request is for. */
export function requestDate(r: Pick<ComeInRequest, 'startsAt'>, timeZone: string): LocalDate {
  return localDateOf(r.startsAt, timeZone)
}

/** `09:00–15:00`, in the zone. */
export function requestWindowLabel(r: Pick<ComeInRequest, 'startsAt' | 'endsAt'>, timeZone: string): string {
  return `${localClockOf(r.startsAt, timeZone)}–${localClockOf(r.endsAt, timeZone)}`
}

export const isOver = (r: Pick<ComeInRequest, 'endsAt'>, now: Instant): boolean => Date.parse(r.endsAt) <= Date.parse(now)

/** Pending or accepted: still a claim on that person's time. */
export const requestIsOpen = (r: Pick<ComeInRequest, 'state'>): boolean => r.state === 'pending' || r.state === 'accepted'

export type RequestWarning = 'unavailable' | 'outside_window' | 'not_set'

export const REQUEST_WARNING_MESSAGE: Readonly<Record<RequestWarning, string>> = {
  unavailable: 'They marked this day as not available. You can still ask.',
  outside_window: 'That is outside the hours they said they can do.',
  not_set: 'They have not said whether they can do this day.',
}

/**
 * What the request form says before it is sent. Never a refusal: asking is
 * always allowed, and the technician is the one who answers.
 */
export function requestWarning(availability: DayAvailability | null | undefined, from: unknown, to: unknown): RequestWarning | null {
  if (!availability) return 'not_set'
  if (availability.status === 'unavailable') return 'unavailable'
  const f = parseClock(from)
  const t = parseClock(to)
  if (!f || !t || !availability.window) return null
  return clockMinutes(f) < clockMinutes(availability.window.from) || clockMinutes(t) > clockMinutes(availability.window.to)
    ? 'outside_window'
    : null
}

/**
 * Against the same technician's other requests: the identical open one (a
 * double tap of Send — return it, do not make a second), or an overlapping
 * open one (refused: two claims on the same hour is a muddle nobody wants to
 * untangle by phone).
 */
export function clashOf<R extends Timed>(
  candidate: Pick<ComeInRequest, 'startsAt' | 'endsAt'>,
  others: readonly R[],
): { readonly kind: 'same' | 'overlaps'; readonly request: R } | null {
  const s = Date.parse(candidate.startsAt)
  const e = Date.parse(candidate.endsAt)
  let overlap: R | null = null
  for (const o of others) {
    if (!requestIsOpen(o)) continue
    const os = Date.parse(o.startsAt)
    const oe = Date.parse(o.endsAt)
    if (os === s && oe === e) return { kind: 'same', request: o }
    if (s < oe && os < e) overlap ??= o
  }
  return overlap ? { kind: 'overlaps', request: overlap } : null
}

type Transition =
  | { readonly ok: true; readonly state: RequestState; readonly changed: boolean; readonly backedOut?: true }
  | { readonly ok: false; readonly reason: ScheduleRefusal }

/**
 * The technician's answer. Pressing the same answer twice is the same answer
 * (a phone that did not show the first tap landing).
 *
 * **Accepted can still become declined, until the shift starts.** Plans
 * change, and the alternative is the phone call this feature exists to
 * replace; `backedOut` says so, so the engineer sees it. Once it has started,
 * not turning up is a conversation, not a button. Declined does not become
 * accepted: by then the engineer may have asked someone else — ask again.
 */
export function answerTransition(
  r: Timed,
  answer: 'accept' | 'decline',
  now: Instant,
): Transition {
  const target: RequestState = answer === 'accept' ? 'accepted' : 'declined'
  if (r.state === 'cancelled') return { ok: false, reason: 'cancelled' }
  if (r.state === target) return { ok: true, state: target, changed: false }
  if (r.state === 'accepted' && target === 'declined') {
    if (Date.parse(r.startsAt) <= Date.parse(now)) return { ok: false, reason: 'already_started' }
    return { ok: true, state: 'declined', changed: true, backedOut: true }
  }
  if (r.state !== 'pending') return { ok: false, reason: 'already_answered' }
  if (isOver(r, now)) return { ok: false, reason: 'already_over' }
  return { ok: true, state: target, changed: true }
}

/** The engineer withdrawing a request, pending or accepted, before it ends. */
export function cancelTransition(r: Timed, now: Instant): Transition {
  if (r.state === 'cancelled') return { ok: true, state: 'cancelled', changed: false }
  if (r.state === 'declined') return { ok: false, reason: 'declined' }
  if (isOver(r, now)) return { ok: false, reason: 'already_over' }
  return { ok: true, state: 'cancelled', changed: true }
}

const byStart = <R extends Pick<ComeInRequest, 'startsAt'>>(a: R, b: R): number => Date.parse(a.startsAt) - Date.parse(b.startsAt)

/**
 * A technician's own list: what needs an answer, and what is booked. Both
 * soonest first, and neither includes anything already over.
 */
export function technicianAgenda<R extends Timed>(
  requests: readonly R[],
  now: Instant,
): { readonly needsAnswer: readonly R[]; readonly comingUp: readonly R[] } {
  const live = requests.filter((r) => !isOver(r, now))
  return {
    needsAnswer: live.filter((r) => r.state === 'pending').sort(byStart),
    comingUp: live.filter((r) => r.state === 'accepted').sort(byStart),
  }
}

// --- The engineer's views ---------------------------------------------------

export interface Technician {
  readonly id: UserId
  readonly name: string
}

export interface GridCell<R> {
  readonly date: LocalDate
  readonly availability: DayAvailability | null
  /** Everything but cancelled requests, soonest first. */
  readonly requests: readonly R[]
}

export interface ScheduleGrid<R, T extends Technician = Technician> {
  readonly dates: readonly LocalDate[]
  readonly rows: readonly { readonly technician: T; readonly cells: readonly GridCell<R>[] }[]
}

/**
 * Technicians down the side, days across the top: what each said about each
 * day, and what each has been asked. A request lands on the local date it
 * starts on, in the configured zone.
 */
export function teamGrid<R extends Timed & Pick<ComeInRequest, 'technicianId'>, T extends Technician>(input: {
  readonly technicians: readonly T[]
  readonly from: LocalDate
  readonly days: number
  readonly availability: readonly DayAvailability[]
  readonly requests: readonly R[]
  readonly timeZone: string
}): ScheduleGrid<R, T> {
  const dates = datesFrom(input.from, input.days)
  const key = (userId: string, date: LocalDate): string => `${userId} ${date}`
  const avail = new Map(input.availability.map((a) => [key(a.userId, a.date), a]))
  const reqs = new Map<string, R[]>()
  for (const r of [...input.requests].sort(byStart)) {
    if (r.state === 'cancelled') continue
    const k = key(r.technicianId, requestDate(r, input.timeZone))
    reqs.set(k, [...(reqs.get(k) ?? []), r])
  }
  return {
    dates,
    rows: input.technicians.map((technician) => ({
      technician,
      cells: dates.map((date) => ({
        date,
        availability: avail.get(key(technician.id, date)) ?? null,
        requests: reqs.get(key(technician.id, date)) ?? [],
      })),
    })),
  }
}

export interface TeamToday<R, T extends Technician = Technician> {
  /** Clocked in right now. A forgotten shift from yesterday is not "on shift". */
  readonly onShift: readonly { readonly technician: T; readonly since: Instant }[]
  /** Accepted for today, soonest first. */
  readonly scheduledToday: readonly R[]
  /** Asked, not answered, not yet over — soonest first. */
  readonly awaiting: readonly R[]
  /** Accepted, then "can't make it", and the day not yet over — soonest first. */
  readonly backedOut: readonly R[]
  /** Said nothing at all about next week — the one gentle nudge. */
  readonly unsetNextWeek: readonly T[]
  readonly nextWeek: { readonly from: LocalDate; readonly to: LocalDate }
}

/** The engineer's block on Today: who is in, who is coming, who has not answered. */
export function teamToday<R extends Timed & Pick<ComeInRequest, 'technicianId' | 'backedOutAt'>, T extends Technician>(input: {
  readonly technicians: readonly T[]
  readonly openShifts: readonly { readonly userId: UserId; readonly startedAt: Instant }[]
  readonly availability: readonly DayAvailability[]
  readonly requests: readonly R[]
  readonly timeZone: string
  readonly now: Instant
}): TeamToday<R, T> {
  const { timeZone, now } = input
  const today = localDateOf(now, timeZone)
  const nextWeek = nextWeekOf(today)
  const people = new Map(input.technicians.map((t) => [t.id as string, t]))
  const onShift = input.openShifts
    .filter((s) => !isForgotten({ startedAt: s.startedAt, endedAt: null }, now))
    .flatMap((s) => {
      const technician = people.get(s.userId)
      return technician ? [{ technician, since: s.startedAt }] : []
    })
    .sort((a, b) => Date.parse(a.since) - Date.parse(b.since))
  const said = new Set(
    input.availability.filter((a) => a.date >= nextWeek.from && a.date <= nextWeek.to).map((a) => a.userId as string),
  )
  return {
    onShift,
    scheduledToday: input.requests
      .filter((r) => r.state === 'accepted' && requestDate(r, timeZone) === today && !isOver(r, now))
      .sort(byStart),
    awaiting: input.requests.filter((r) => r.state === 'pending' && !isOver(r, now)).sort(byStart),
    backedOut: input.requests.filter((r) => backedOut(r) && !isOver(r, now)).sort(byStart),
    unsetNextWeek: input.technicians.filter((t) => !said.has(t.id)),
    nextWeek,
  }
}
